import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { appContext } from 'common/contexts';
import { Routes as R } from 'common/enums';
import { PlusTwoTone, EditSquareOutlineTwoTone, TrashCanOutlineTwoTone } from 'common/icons';
import { InventoryItem } from 'common/types';
import InventoryNav from './inventoryNav';
import styles from './inventoryItemsList.module.css';

const InventoryItemsList: React.FC = () => {
  const { state, actions, helpers } = useContext(appContext);
  const { inventoryItems = [] } = state;
  const { translation } = helpers;
  const { t } = translation;
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Forzar actualización cuando cambian los items de inventario
  useEffect(() => {
    console.log('Inventario actualizado en el componente:', inventoryItems.length, 'items');
    console.log('Inventario actualizado en el componente:', inventoryItems);
    setLastUpdate(Date.now());
  }, [inventoryItems]);

  // Filter out deleted items
  const activeItems = inventoryItems.filter(item => !item.isDeleted);

  const handleDelete = async (itemId: string) => {
    if (window.confirm(t('common.confirm'))) {
      try {
        setLoading(true);
        await actions.inventory.removeInventoryItem(itemId);
      } catch (error) {
        console.error('Error deleting inventory item:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const getStockStatus = (item: InventoryItem) => {
    if (item.quantity <= 0) {
      return {
        label: t('admin.inventory.items.outOfStock'),
        className: styles.outOfStock
      };
    } else if (item.quantity <= item.minQuantity) {
      return {
        label: t('admin.inventory.items.lowStock'),
        className: styles.lowStock
      };
    } else {
      return {
        label: t('admin.inventory.items.inStock'),
        className: styles.inStock
      };
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('admin.inventory.title')}</h1>
      </div>

      <InventoryNav />

      <div className={styles.header}>
        <h2 className={styles.title}>{t('admin.inventory.items.title')}</h2>
        <Link to={R.InventoryItemCreate} className={styles.addButton}>
          <PlusTwoTone className={styles.addButtonIcon} />
          {t('admin.inventory.items.addItemLabel')}
        </Link>
      </div>

      <div className={styles.tableContainer}>
        {activeItems.length > 0 ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('admin.inventory.items.type')}</th>
                <th>{t('admin.inventory.items.quantity')}</th>
                <th>{t('admin.inventory.items.unit')}</th>
                <th>{t('admin.inventory.items.minQuantity')}</th>
                <th>{t('admin.inventory.items.costPrice')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {activeItems.map((item) => {
                const status = getStockStatus(item);
                return (
                  <tr key={`${item.id}-${lastUpdate}`}>
                    <td>{item.name}</td>
                    <td>
                      {item.type === 'raw'
                        ? t('admin.inventory.items.raw')
                        : t('admin.inventory.items.finished')}
                    </td>
                    <td className={styles.quantityCell}>{parseFloat(item.quantity.toString()).toFixed(2)}</td>
                    <td>{item.unit}</td>
                    <td>{item.minQuantity}</td>
                    <td>{item.costPrice}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <Link
                          to={R.InventoryItemEdit.replace(':id', item.id)}
                          className={styles.actionButton}
                        >
                          <EditSquareOutlineTwoTone />
                        </Link>
                        <button
                          className={styles.actionButton}
                          onClick={() => handleDelete(item.id)}
                          disabled={loading}
                        >
                          <TrashCanOutlineTwoTone />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📦</div>
            <p>{t('admin.inventory.items.emptyListMessage')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryItemsList;
